"use client";

import { useEffect, useState, useLayoutEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { getLastOrganization } from "@/lib/preferences";

// Key for storing auth hint in localStorage
const AUTH_HINT_KEY = 'design-commons-auth-hint';

export function getAuthHint(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(AUTH_HINT_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setAuthHint(isAuthenticated: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (isAuthenticated) {
      localStorage.setItem(AUTH_HINT_KEY, 'true');
    } else {
      localStorage.removeItem(AUTH_HINT_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
}

export function AuthRedirect() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect");
  const [hasRedirected, setHasRedirected] = useState(false);

  // Instant redirect using localStorage hint - runs synchronously before paint
  useLayoutEffect(() => {
    // Skip if we've already initiated a redirect
    if (hasRedirected) return;

    // Check if we have a localStorage hint that user is authenticated
    const authHint = getAuthHint();
    const lastOrg = getLastOrganization();

    // If we have auth hint AND last org, redirect immediately
    // This gives the instant experience for logged-in users
    if (authHint && lastOrg && !redirect) {
      setHasRedirected(true);
      router.replace(`/@${lastOrg.slug}/feed`);
    }
  }, [router, redirect, hasRedirected]);

  // Standard auth-based redirect as fallback
  useEffect(() => {
    if (hasRedirected) return;

    if (!loading && user) {
      // Update auth hint when we confirm user is logged in
      setAuthHint(true);

      if (redirect) {
        router.push(redirect);
      } else {
        const lastOrg = getLastOrganization();
        if (lastOrg) {
          router.push(`/@${lastOrg.slug}/feed`);
        } else {
          router.push("/orgs/select");
        }
      }
      setHasRedirected(true);
    }
  }, [user, loading, router, redirect, hasRedirected]);

  return null;
}
